// A panel anchored to a control. The screen-anchored half of PLAN-UI §28.1's
// two primitives — the hotbar's build flyout, the orders list, the selection's
// `···` context panel (`#flyout`, `#orders`, `#ctx` in `docs/ui/screen-mock.html`).
//
// **Popper, not MUI's `Popover`.** `Popover` is a `Modal`: it traps focus,
// renders a backdrop and blocks the page behind it. Every popover in the mock
// is the opposite of that — the board stays live underneath, the camera keeps
// turning, and the panel is dismissed by clicking away from it. `Popper` is
// the positioning engine on its own, which is the part that is genuinely hard
// (flip, shift, portal) and the only part wanted.
//
// So dismissal is assembled here rather than inherited: `ClickAwayListener`
// for the click, a key listener for Escape. Both are the caller's `onClose` —
// this component holds no open state of its own, because the control that
// opens it is the control that has to look pressed while it is open.

import { ClickAwayListener, Popper, type PopperPlacementType, keyframes, styled } from "@mui/material";
import { type ReactNode, useEffect } from "react";

import { Panel } from "./Panel";
import { REDUCED_MOTION, STILL } from "./motion";

const rise = keyframes({
  from: { opacity: 0, transform: "scale(.96)" },
  to: { opacity: 1, transform: "scale(1)" }
});

// The surface is a `Panel` — the mock's `.panel` rule, already in tokens there
// — plus the two things a popover adds: padding and an entry animation.
const Surface = styled(Panel)(({ theme }) => ({
  padding: theme.kit.space.md,
  // **Entry only.** A panel that animates out has to stay mounted while it
  // does, and a dismissal the player has already made is not a thing to wait
  // for — the click that closed this is usually a click on something else.
  animation: `${rise} ${theme.kit.motion.duration.fast}ms ${theme.kit.motion.easeBounce} both`,
  [REDUCED_MOTION]: STILL
}));

// Popper positions itself through inline `style`, so its z-index is the one
// thing that has to be a class — set it in `style` and the transform goes with
// it. One rung for every popover in the app; see `tokens.ts`.
const Positioned = styled(Popper)(({ theme }) => ({ zIndex: theme.kit.z.popover }));

export interface PopoverProps {
  open: boolean;
  /** The control it hangs off. Null keeps it shut. */
  anchorEl: HTMLElement | null;
  /** Called for a click away and for Escape. The caller owns `open`. */
  onClose(): void;
  /** Popper's own placements. Default `bottom-start`. */
  placement?: PopperPlacementType;
  children: ReactNode;
  "data-testid"?: string;
}

export function Popover({
  open,
  anchorEl,
  onClose,
  placement = "bottom-start",
  children,
  "data-testid": testId
}: PopoverProps) {
  // Listened for only while open, so a screen with six shut popovers on it
  // does not put six handlers on the document.
  //
  // **Capture, and it stops propagation**, which is the difference between
  // Escape closing this and Escape closing this *and* opening the pause menu
  // behind it. `GameShell` listens for Escape on `window` too, and it
  // registers first — so `stopImmediatePropagation` from a bubble listener
  // here would be too late, and both handlers ran. A capture listener on
  // `window` runs before every bubble listener on the way back up, so
  // `stopPropagation` there is what actually consumes the key.
  //
  // An open popover consuming Escape is the right rule and not merely the
  // convenient one: it is the innermost thing on screen, and the key means
  // "shut the innermost thing".
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  return (
    <Positioned open={open && anchorEl !== null} anchorEl={anchorEl} placement={placement}>
      <ClickAwayListener
        onClickAway={(event) => {
          // **The anchor is not "away".** A toggle button closes this itself,
          // and click-away firing first would close it a moment before the
          // button's own handler opened it again — the panel that will not
          // shut, which is how every hand-rolled version of this ends up.
          if (anchorEl?.contains(event.target as Node)) return;
          onClose();
        }}
      >
        <Surface data-testid={testId}>{children}</Surface>
      </ClickAwayListener>
    </Positioned>
  );
}
